import { VisualizationType } from '@/api';
import Visualization from '@/services/visualization';
import { assertIsDefined } from '@/utils/typeChecks';
import type { FormatOptions } from '@/utils/formatValue';
import type { Sorting } from '@/models/objects/datasetViewState';

export const SERIE_TYPES = ['bar', 'line'] as const;

export const AGGREGATE_FUNCTIONS = [
  'AVG',
  'COUNT',
  'MAX',
  'MEDIAN',
  'MIN',
  'SUM',
  'STDDEV_SAMP',
  'STDDEV_POP',
  'VAR_SAMP',
  'VAR_POP',
] as const;

export const METRIC_OPTIONS = [
  { label: 'SUM', value: 'SUM' },
  { label: 'AVERAGE', value: 'AVG' },
  { label: 'MEDIAN', value: 'MEDIAN' },
  { label: 'MIN', value: 'MIN' },
  { label: 'MAX', value: 'MAX' },
  { label: 'COUNT', value: 'COUNT' },
  { label: 'ANY_VALUE', value: 'ANY_VALUE' },
];

export const DATA_TYPES = ['string', 'number', 'date'] as const;

export const TITLE_ALIGNMENTS = ['left', 'center', 'right'] as const;
export type TitleAlignment = typeof TITLE_ALIGNMENTS[number];

export const DELTA_INDICATORS = ['arrows', 'symbols'] as const;
export type DeltaIndicator = typeof DELTA_INDICATORS[number];

export const CONDITIONAL_OPERATORS = ['gt', 'gte', 'lt', 'lte', 'eq', 'neq', 'between'] as const;
export type ConditionalOperator = typeof CONDITIONAL_OPERATORS[number];

export type ConditionalRule = {
  operator: ConditionalOperator;
  value: number;
  value2?: number; // For 'between' operator
  backgroundColor?: string;
  textColor?: string;
};

export type ColumnFormatOptions = FormatOptions & {
  conditionalRules?: ConditionalRule[];
};

export type ColumnFormatConfig = Record<string, ColumnFormatOptions>;

export type SeriesField = {
  name: string;
  type: typeof SERIE_TYPES[number];
  aggregate?: typeof AGGREGATE_FUNCTIONS[number];
}[];

export type FormFields = {
  // Base chart fields
  title: string;
  titleAlignment: TitleAlignment;
  type: VisualizationType;
  groupBy?: string;
  xAxisColumn?: string;
  yAxisColumns: string[];
  yAxisColumn?: string;
  xLabel: string;
  xAxisDataType?: typeof DATA_TYPES[number];
  yLabel: string;
  yAxisDataType?: typeof DATA_TYPES[number];
  isStacked: boolean;
  isHorizontal: boolean;
  series: SeriesField;
  // Metric fields
  column?: string;
  metric?: string;
  description: string;
  prefix?: string;
  suffix?: string;
  valueFormat?: string;
  decimals?: number;
  currency?: string;
  maxValue: number;
  maxValueColumn?: string;
  maxValueAgg?: string;
  descValueColumn?: string;
  descValueAgg?: string;
  orderByColumn?: string;
  sortDirection?: 'ASC' | 'DESC';
  // Comparison/Delta fields
  goalValue?: number;
  goalValueColumn?: string;
  deltaIndicator?: DeltaIndicator;
  showComparisonValue?: boolean;
  hideTitle?: boolean;
  descriptionBelowChart?: boolean;
  customQuery?: string;
  // Table fields
  columns: string[];
  headerBackgroundColor?: string;
  headerFontColor?: string;
  showRowNumbers?: boolean;
  columnFormats?: ColumnFormatConfig;
  columnTotals: string[];
  sortings: Sorting[];
  hidePaginationWhenSinglePage?: boolean;
  hideHiddenSortColumns?: boolean;
};

export const VIZ_FORM_DEFAULTS: FormFields = {
  title: '',
  titleAlignment: 'left',
  type: VisualizationType.BAR_CHART,
  groupBy: undefined,
  xAxisColumn: undefined,
  yAxisColumns: [],
  yAxisColumn: undefined,
  xLabel: '',
  xAxisDataType: undefined,
  yLabel: '',
  yAxisDataType: undefined,
  isStacked: false,
  isHorizontal: false,
  series: [],
  column: undefined,
  metric: undefined,
  description: '',
  prefix: '',
  suffix: '',
  valueFormat: 'none',
  decimals: 2,
  currency: 'USD',
  maxValue: 100,
  maxValueColumn: undefined,
  maxValueAgg: undefined,
  descValueColumn: undefined,
  descValueAgg: undefined,
  orderByColumn: undefined,
  sortDirection: undefined,
  goalValue: undefined,
  goalValueColumn: undefined,
  deltaIndicator: 'arrows',
  showComparisonValue: true,
  hideTitle: false,
  descriptionBelowChart: false,
  customQuery: '',
  columns: [],
  headerBackgroundColor: '#FFFFFF',
  headerFontColor: '#000000',
  showRowNumbers: true,
  columnFormats: {},
  columnTotals: [],
  sortings: [],
  hidePaginationWhenSinglePage: false,
  hideHiddenSortColumns: false,
};

export function getInitialValues(
  mode: 'create' | 'update',
  viz: Visualization | undefined,
): FormFields {
  if (mode === 'create' || !viz) {
    return { ...VIZ_FORM_DEFAULTS };
  }

  // In update mode, ensure viz is defined.
  assertIsDefined(viz, 'Visualization is required in update mode');
  const { name, type, configurations } = viz;
  const tableConfig =
    type === VisualizationType.TABLE
      ? (configurations as { columns: string[]; columnTotals?: string[]; sortings?: Sorting[] })
      : null;

  const initialValues = {
    ...VIZ_FORM_DEFAULTS,
    ...configurations,
    // Ensure columnTotals is always an array for backward compatibility (only for table configurations)
    columnTotals: tableConfig?.columnTotals || [],
    // Ensure sortings is always an array (stale entries are cleaned up by SortConfigurationPanel
    // once allDatasetColumns is available, which also covers sort-only/hidden columns)
    sortings: tableConfig?.sortings || [],
    title: name,
    type,
  };

  // No need to reset gauge chart values - they're already set from configurations above

  return initialValues;
}
